import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ConfirmDialogComponent, ConfirmDialogModel } from '../../components/modal/confirmdialog/confirmdialog.component';
import { SnackBarComponent } from '../../components/snackbar/snackbar.component';
import { TeamMember } from '../../interfaces/team/member/member.interface';
import { TeamInterface } from '../../interfaces/team/team.interface';
import { SpinnerService } from '../spinner/spinner.service';
import { StripeService } from '../stripe/stripe.service';
import { TeamsService } from '../teams/teams.service';
import { UiAlertService } from '../ui-alert/ui-alert.service';

export type TeamMemberRemovalMutation = 'deactivate' | 'remove';
export type TeamMemberRemovalOperation =
  | 'balanceReset'
  | 'deactivatedPendingSettlement'
  | 'finalized'
  | 'removed';

export interface TeamMemberRemovalRequest {
  team: TeamInterface;
  member: TeamMember;
  dontDelete?: boolean;
  confirmationWarning?: string;
  beforeMutation?: (mutation: TeamMemberRemovalMutation) => Promise<void>;
}

export interface TeamMemberRemovalResult {
  status: 'cancelled' | 'failed' | 'succeeded';
  operation?: TeamMemberRemovalOperation;
  preparationApplied?: true;
}

@Injectable({ providedIn: 'root' })
export class TeamMemberRemovalCoordinator {
  constructor(
    private readonly teamsService: TeamsService,
    private readonly stripeService: StripeService,
    private readonly uiAlertService: UiAlertService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly spinnerService: SpinnerService,
  ) {}

  isDeactivationPendingSettlement(member: TeamMember): boolean {
    return (
      member?.removalStatus === 'deactivationPendingSettlement' ||
      member?.metadata?.removalStatus === 'deactivationPendingSettlement' ||
      member?.teamuserMetadata?.removalStatus === 'deactivationPendingSettlement'
    );
  }

  async removeMember(request: TeamMemberRemovalRequest): Promise<TeamMemberRemovalResult> {
    const { member, team } = request;
    let beforeMutationCalled = false;
    let preparationApplied = false;
    let spinnerShown = false;
    let failureMessage = 'Unable to remove this team member.';

    const beforeMutation = async (mutation: TeamMemberRemovalMutation): Promise<void> => {
      if (!request.beforeMutation || beforeMutationCalled) {
        return;
      }
      beforeMutationCalled = true;
      failureMessage = 'Unable to prepare this team member for removal.';
      await request.beforeMutation(mutation);
      preparationApplied = true;
    };

    try {
      if (this.isDeactivationPendingSettlement(member)) {
        const confirmFinalize = await this.uiAlertService.presentAlertConfirm(
          'Finalize removal for this team member? This can only complete after pending ACH settlement.',
        );
        if (!confirmFinalize) {
          return { status: 'cancelled' };
        }

        failureMessage = 'Unable to finalize this team member removal.';
        await this.teamsService.finalizeTeamMemberRemoval(member.memberId, member.teamId);
        this.showSnack('Team member removed');
        return { status: 'succeeded', operation: 'finalized' };
      }

      const amountToRefund = this.getAmountToRefund(member);
      const confirm = await this.uiAlertService.presentAlertConfirm(
        this.getRemovalConfirmationMessage(
          member,
          amountToRefund,
          request.confirmationWarning,
        ),
      );
      if (!confirm) {
        return { status: 'cancelled' };
      }

      const leavingCompany = await this.resolveLeavingCompany(member);
      if (leavingCompany === null) {
        return { status: 'cancelled' };
      }

      this.spinnerService.show('Loading...');
      spinnerShown = true;
      failureMessage = 'Unable to check pending team activity.';
      const pendingCharge = await this.teamsService.checkTeamPendingCharges(member.teamId);
      if (pendingCharge?.hasPendingCharges) {
        const deactivationResult = await this.confirmAndDeactivate(
          member,
          leavingCompany,
          beforeMutation,
          (message) => failureMessage = message,
        );
        return this.withPreparation(deactivationResult, preparationApplied);
      }

      if (parseFloat(member.balance) > 0) {
        failureMessage = 'Unable to refund this team member\'s remaining balance.';
        const customerId = await this.teamsService.getCustomerId(team);
        await this.stripeService.issueRefund(customerId, amountToRefund);
        this.showSnack(
          `This team member has a remaining balance of $${amountToRefund.toFixed(
            2,
          )} which will be refunded to your default payment method.`,
        );

        if (request.dontDelete) {
          failureMessage = 'Unable to update this team member after the refund.';
          await this.teamsService.modifyTeamMember(team.id, member.memberId, { balance: 0 });
          return { status: 'succeeded', operation: 'balanceReset' };
        }
      }

      const removalResult = await this.removeOrOfferPendingDeactivation(
        member,
        leavingCompany,
        beforeMutation,
        (message) => failureMessage = message,
      );
      return this.withPreparation(removalResult, preparationApplied);
    } catch (_) {
      this.showSnack(failureMessage);
      return this.withPreparation({ status: 'failed' }, preparationApplied);
    } finally {
      if (spinnerShown) {
        this.spinnerService.hide();
      }
    }
  }

  private getAmountToRefund(member: TeamMember): number {
    let amountToRefund = 0;
    if (member.p2pBalance && parseFloat(member.p2pBalance) > 0) {
      amountToRefund += parseFloat(member.p2pBalance);
    }
    if (parseFloat(member?.balance) > 0) {
      amountToRefund += parseFloat(member.balance);
    }
    return amountToRefund;
  }

  private getRemovalConfirmationMessage(
    member: TeamMember,
    amountToRefund: number,
    confirmationWarning?: string,
  ): string {
    const message = 'Are you sure you want to remove this team member?';
    const hasP2pBalance = !!member.p2pBalance && parseFloat(member.p2pBalance) > 0;
    const refundMessage = amountToRefund > 0
      ? hasP2pBalance
        ? ` Their remaining allowance and peer recognition balance totaling $${amountToRefund.toFixed(2)} will be refunded.`
        : ` Their remaining allowance balance totaling $${amountToRefund.toFixed(2)} will be refunded.`
      : '';
    const warningMessage = confirmationWarning ? ` ${confirmationWarning}` : '';
    return `${message}${refundMessage}${warningMessage}`;
  }

  private withPreparation(
    result: TeamMemberRemovalResult,
    preparationApplied: boolean,
  ): TeamMemberRemovalResult {
    return preparationApplied ? { ...result, preparationApplied: true } : result;
  }

  private async resolveLeavingCompany(member: TeamMember): Promise<boolean | null> {
    if (!member.userId) {
      return false;
    }

    const removalContext = await this.teamsService.getTeamMemberRemovalContext(
      member.memberId,
      member.teamId,
    );
    if (!removalContext.isLastCompanyTeamMembership) {
      return false;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean | null>(
      ConfirmDialogComponent,
      {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Is this employee leaving the company?',
          'Choose “Leaving company” only if their employment has ended. Wallit will then help them move their login to a verified backup email.',
          false,
          'Leaving company',
          'Team only',
          false,
          'Cancel',
        ),
      },
    );

    const result = await dialogRef.afterClosed().toPromise();
    return result === true ? true : result === false ? false : null;
  }

  private async confirmAndDeactivate(
    member: TeamMember,
    leavingCompany: boolean,
    beforeMutation: (mutation: TeamMemberRemovalMutation) => Promise<void>,
    setFailureMessage: (message: string) => void,
  ): Promise<TeamMemberRemovalResult> {
    const confirmed = await this.confirmPendingSettlementDeactivation();
    if (!confirmed) {
      this.showSnack(
        'A transfer is in progress at the moment. Final removal can be completed after settlement.',
      );
      return { status: 'cancelled' };
    }

    await beforeMutation('deactivate');
    setFailureMessage('Unable to deactivate this team member pending settlement.');
    await this.teamsService.deactivateTeamMemberPendingSettlement(
      member.memberId,
      member.teamId,
      leavingCompany,
    );
    this.showSnack('Team member deactivated pending settlement');
    return { status: 'succeeded', operation: 'deactivatedPendingSettlement' };
  }

  private async removeOrOfferPendingDeactivation(
    member: TeamMember,
    leavingCompany: boolean,
    beforeMutation: (mutation: TeamMemberRemovalMutation) => Promise<void>,
    setFailureMessage: (message: string) => void,
  ): Promise<TeamMemberRemovalResult> {
    await beforeMutation('remove');
    setFailureMessage('Unable to remove this team member.');

    try {
      await this.teamsService.removeTeamMember(member.memberId, member.teamId, leavingCompany);
      this.showSnack('Team member removed');
      return { status: 'succeeded', operation: 'removed' };
    } catch (error: any) {
      const data = error?.data || error?.error;
      if (data?.code !== 'PENDING_ACH_TRANSFERS' || !data?.canDeactivatePendingSettlement) {
        throw error;
      }

      return this.confirmAndDeactivate(
        member,
        leavingCompany,
        beforeMutation,
        setFailureMessage,
      );
    }
  }

  private async confirmPendingSettlementDeactivation(): Promise<boolean> {
    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(
      ConfirmDialogComponent,
      {
        data: new ConfirmDialogModel(
          'Deactivate Member?',
          'This team has pending ACH activity. You can deactivate this member now so they stop receiving new rewards, allowances, and reimbursements. Final removal will complete after settlement.',
          false,
          'Deactivate now',
          'Cancel',
        ),
      },
    );
    return !!(await dialogRef.afterClosed().toPromise());
  }

  private showSnack(message: string): void {
    this.snackBar.openFromComponent(SnackBarComponent, { data: message });
  }
}
